import React, {useState, useEffect, useMemo} from 'react';
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Search,
  ContentCopy,
  Visibility,
  Lock,
  Info,
  Download,
  Upload,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
  Close,
} from '@mui/icons-material';
import {Checkbox} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {Axios} from 'services/axios';
import {Block, Confirm} from 'notiflix';
import {useNavigate} from 'react-router-dom';
import MDBadge from 'components/MDBadge';

const DuplicateNoticeDialog = ({open, onClose, onConfirm, template}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}>
      <DialogTitle sx={{py: 2.5, px: 3, borderBottom: '1px solid', borderColor: 'divider'}}>
        <Box display="flex" alignItems="center" gap={1}>
          <ContentCopy fontSize="small" sx={{color: 'text.secondary'}} />
          <MDTypography variant="h6">
            Duplicate Template
          </MDTypography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{pt: 3, px: 3}}>
        <MDTypography variant="body2" color="text" paragraph>
          You are about to create a copy of <strong>"{template?.name}"</strong>. All fields and content will be duplicated.
        </MDTypography>

        <Divider sx={{my: 2}} />

        <MDTypography variant="subtitle2" gutterBottom>
          Fields that must be updated after duplicating:
        </MDTypography>

        <Box component="ul" sx={{pl: 2.5, mb: 2, mt: 1}}>
          {[
            ['Template Name', 'Give it a unique, descriptive name'],
            ['Category', 'Select the correct category for this template'],
            ['Type Key', 'Select appropriate type key for the category'],
            ['Country', 'Select the country this template belongs to'],
          ].map(([field, desc]) => (
            <Box component="li" key={field} sx={{mb: 0.75}}>
              <MDTypography variant="body2" color="text">
                <strong>{field}</strong> — {desc}
              </MDTypography>
            </Box>
          ))}
        </Box>

        <MDTypography variant="caption" color="text" sx={{display: 'block', mt: 1}}>
          All other data (field definitions, settings, default content) will be preserved from the original.
        </MDTypography>

        <Divider sx={{my: 2}} />

        <Paper variant="outlined" sx={{p: 2}}>
          <MDTypography variant="caption" color="text" fontWeight="bold" sx={{display: 'block', mb: 1}}>
            Original Template
          </MDTypography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <MDTypography variant="caption" color="text">Name</MDTypography>
              <MDTypography variant="body2">{template?.name}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="caption" color="text">Category</MDTypography>
              <MDTypography variant="body2" sx={{textTransform: 'capitalize'}}>{template?.category}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="caption" color="text">Type Key</MDTypography>
              <MDTypography variant="body2">{template?.typeKey}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="caption" color="text">Fields</MDTypography>
              <MDTypography variant="body2">{template?.fieldDefinitions?.length || 0}</MDTypography>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{p: 2.5, gap: 1, borderTop: '1px solid', borderColor: 'divider'}}>
        <MDButton onClick={onClose} variant="outlined" color="secondary">
          Cancel
        </MDButton>
        <MDButton
          onClick={() => onConfirm(template)}
          variant="gradient"
          color="info"
          startIcon={<ContentCopy />}>
          Continue to Duplicate
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

function SectionTemplateList() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [importError, setImportError] = useState('');
  
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [templateToDuplicate, setTemplateToDuplicate] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelectMode = () => {
    setSelectMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleSelectId = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredTemplates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTemplates.map(t => t.id)));
    }
  };

  const handleBulkExport = () => {
    const list = filteredTemplates
      .filter(t => selectedIds.has(t.id))
      .map(t => ({
        name: t.name, typeKey: t.typeKey, description: t.description,
        category: t.category, fieldDefinitions: t.fieldDefinitions,
        defaultContent: t.defaultContent, settingsSchema: t.settingsSchema,
        isSystem: false, isLocked: false, scope: t.scope,
        // countryCode ("IN", "US") travels with the export instead of the
        // raw countryId — countryId is an environment-specific UUID that
        // won't exist when this file is imported into a different
        // environment (e.g. local export imported into production).
        countryCode: t.country?.countryCode || '',
        allowedPageIds: t.allowedPageIds || [],
        maxUsagePerPage: t.maxUsagePerPage,
      }));
    const blob = new Blob([JSON.stringify(list, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'selected_templates_export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkDelete = () => {
    const count = selectedIds.size;
    Confirm.show(
      'Delete Selected Templates',
      `Are you sure you want to delete ${count} selected template${count > 1 ? 's' : ''}?`,
      'Yes, Delete',
      'Cancel',
      async () => {
        try {
          Block.circle('#templates-container', 'Deleting...');
          await Promise.all(
            [...selectedIds].map(id => {
              const label = templates.find(t => t.id === id)?.name || '';
              return Axios.delete(`section-templates/${id}?label=${encodeURIComponent(label)}`);
            }),
          );
          setSelectedIds(new Set());
          setSelectMode(false);
          fetchTemplates(categoryFilter);
        } catch (error) {
          alert(error.response?.data?.message || 'Error deleting templates');
        } finally {
          Block.remove('#templates-container');
        }
      },
    );
  };

  const categories = [
    'all',
    'hero',
    'services',
    'gallery',
    'testimonials',
    'faq',
    'contact',
    'cta',
    'features',
    'custom',
  ];

  const fetchTemplates = async (category = 'all') => {
    try {
      setLoading(true);
      const res = await Axios.fetch('section-templates', {
        params: {
          category: category !== 'all' ? category : undefined,
          limit: 50,
        },
      });
      setTemplates(res.data.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates(categoryFilter);
  }, [categoryFilter]);

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      t =>
        t.name?.toLowerCase().includes(term) ||
        t.type?.toLowerCase().includes(term) ||
        t.category?.toLowerCase().includes(term),
    );
  }, [templates, search]);

  useEffect(() => {
    if (loading) {
      Block.circle('#templates-container', 'Loading Templates...');
    } else {
      Block.remove('#templates-container');
    }
  }, [loading]);

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleDuplicateClick = () => {
    setTemplateToDuplicate(selectedTemplate);
    handleMenuClose(); 
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = async (template) => {
    try {
      Block.circle('#templates-container', 'Creating duplicate...');
      
      const response = await Axios.post(`section-templates/${template.id}/duplicate`);
      const duplicatedTemplate = response.data.data.template;
      
      Block.remove('#templates-container');
      setDuplicateDialogOpen(false);
      setTemplateToDuplicate(null);
      alert('Template duplicated successfully! Please update the required fields.');
      
      navigate(`/section-templates/${duplicatedTemplate.id}/edit`);
      
    } catch (error) {
      console.error('Duplicate error:', error);
      Block.remove('#templates-container');
      alert(error.response?.data?.message || 'Error duplicating template');
    }
  };

  const handleExportAll = () => {
    const exportList = templates.map(t => ({
      name: t.name,
      typeKey: t.typeKey,
      description: t.description,
      category: t.category,
      fieldDefinitions: t.fieldDefinitions,
      defaultContent: t.defaultContent,
      settingsSchema: t.settingsSchema,
      isSystem: false,
      isLocked: false,
      scope: t.scope,
      // countryCode ("IN", "US") travels with the export instead of the raw
      // countryId — countryId is an environment-specific UUID that won't
      // exist when this file is imported into a different environment.
      countryCode: t.country?.countryCode || '',
      allowedPageIds: t.allowedPageIds || [],
      maxUsagePerPage: t.maxUsagePerPage,
    }));
    const blob = new Blob([JSON.stringify(exportList, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'section_templates_export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const template = selectedTemplate;
    handleMenuClose();
    const exportData = {
      name: template.name,
      typeKey: template.typeKey,
      description: template.description,
      category: template.category,
      fieldDefinitions: template.fieldDefinitions,
      defaultContent: template.defaultContent,
      settingsSchema: template.settingsSchema,
      isSystem: false,
      isLocked: false,
      scope: template.scope,
      // countryCode ("IN", "US") travels with the export instead of the raw
      // countryId — countryId is an environment-specific UUID that won't
      // exist when this file is imported into a different environment.
      countryCode: template.country?.countryCode || '',
      allowedPageIds: template.allowedPageIds || [],
      maxUsagePerPage: template.maxUsagePerPage,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name.replace(/\s+/g, '_')}_template.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importSingleTemplate = async data => {
    const pageIds = Array.isArray(data.allowedPageIds)
      ? data.allowedPageIds.filter(Boolean)
      : [];
    // Exports without page IDs cannot stay page-specific — API requires at least one page.
    const scope =
      data.scope === 'page-specific' && pageIds.length > 0
        ? 'page-specific'
        : 'global';

    const formData = new FormData();
    formData.append('name', `${data.name} (Imported)`);
    formData.append('typeKey', data.typeKey);
    formData.append('description', data.description || '');
    formData.append('category', data.category);
    formData.append('isSystem', 'false');
    formData.append('isLocked', String(data.isLocked || false));
    formData.append('scope', scope);
    // Prefer countryCode (portable across environments) over a raw
    // countryId, which is an environment-specific UUID and won't resolve
    // when importing a template exported from a different environment.
    if (data.countryCode) {
      formData.append('countryCode', data.countryCode);
    } else if (data.countryId) {
      formData.append('countryId', data.countryId);
    }
    if (scope === 'page-specific') {
      formData.append('allowedPageIds', JSON.stringify(pageIds));
    }
    if (data.fieldDefinitions) formData.append('fieldDefinitions', JSON.stringify(data.fieldDefinitions));
    if (data.defaultContent) formData.append('defaultContent', JSON.stringify(data.defaultContent));
    if (data.settingsSchema) formData.append('settingsSchema', JSON.stringify(data.settingsSchema));
    if (data.maxUsagePerPage != null) formData.append('maxUsagePerPage', data.maxUsagePerPage);
    const response = await Axios.post('section-templates', formData, {'Content-Type': 'multipart/form-data'});
    return response.data.data?.template || response.data.data;
  };

  const handleImport = async e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setImportError('');
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed) ? parsed : [parsed];

      const invalid = list.find(d => !d.name || !d.typeKey || !d.category);
      if (invalid) {
        setImportError('Invalid template file: missing required fields (name, typeKey, category).');
        return;
      }

      Block.circle('#templates-container', 'Importing template...');
      const results = await Promise.all(list.map(importSingleTemplate));
      Block.remove('#templates-container');
      fetchTemplates(categoryFilter);
      if (results.length === 1) {
        navigate(`/section-templates/${results[0].id}/edit`);
      }
    } catch (err) {
      Block.remove('#templates-container');
      setImportError(err.response?.data?.message || err.message || 'Failed to import template.');
    }
  };

  const handleDelete = async () => {
    Confirm.show(
      'Delete Template',
      `Are you sure you want to delete "${selectedTemplate.name}"?`,
      'Yes, Delete',
      'Cancel',
      async () => {
        try {
          Block.circle('#templates-container');
          await Axios.delete(
            `section-templates/${selectedTemplate.id}?label=${encodeURIComponent(selectedTemplate.name)}`,
          );
          fetchTemplates(categoryFilter);
        } catch (error) {
          console.error('Delete error:', error);
          alert(error.response?.data?.message || 'Error deleting template');
        } finally {
          Block.remove('#templates-container');
          handleMenuClose();
        }
      },
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="templates-container">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center">
                <MDTypography variant="h6" color="white">
                  Section Templates
                </MDTypography>
                <Box display="flex" gap={1} alignItems="center">
                  {selectMode ? (
                    <>
                      <MDTypography variant="caption" color="white" sx={{mr: 1}}>
                        {selectedIds.size} selected
                      </MDTypography>
                      <MDButton
                        variant="outlined"
                        color="white"
                        startIcon={<Upload />}
                        onClick={handleBulkExport}
                        disabled={selectedIds.size === 0}>
                        Export Selected
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="white"
                        startIcon={<Delete />}
                        onClick={handleBulkDelete}
                        disabled={selectedIds.size === 0}
                        sx={{'&:not(:disabled)': {borderColor: '#ff6b6b', color: '#ff6b6b'}}}>
                        Delete Selected
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="white"
                        startIcon={<Close />}
                        onClick={toggleSelectMode}>
                        Cancel
                      </MDButton>
                    </>
                  ) : (
                    <>
                      <MDButton
                        variant="outlined"
                        color="white"
                        component="label"
                        startIcon={<Download />}>
                        Import
                        <input type="file" accept=".json" hidden onChange={handleImport} />
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="white"
                        startIcon={<Upload />}
                        onClick={handleExportAll}
                        disabled={templates.length === 0}>
                        Export All
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="white"
                        startIcon={<CheckBoxOutlineBlank />}
                        onClick={toggleSelectMode}>
                        Select
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={() => navigate('/section-templates/new')}
                        startIcon={<Add />}>
                        Create Template
                      </MDButton>
                    </>
                  )}
                </Box>
              </MDBox>

              <MDBox p={3}>
                {selectMode && filteredTemplates.length > 0 && (
                  <Box display="flex" alignItems="center" mb={2}>
                    <Checkbox
                      checked={selectedIds.size === filteredTemplates.length}
                      indeterminate={selectedIds.size > 0 && selectedIds.size < filteredTemplates.length}
                      onChange={toggleSelectAll}
                      size="small"
                    />
                    <MDTypography variant="caption" color="text">
                      {selectedIds.size === filteredTemplates.length ? 'Deselect All' : 'Select All'}
                    </MDTypography>
                  </Box>
                )}
                {importError && (
                  <Alert severity="error" onClose={() => setImportError('')} sx={{mb: 2}}>
                    {importError}
                  </Alert>
                )}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search templates by name or type..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox p={3}>
                <Grid container spacing={2}>
                  {filteredTemplates.length === 0 ? (
                    <Grid item xs={12}>
                      <Paper sx={{p: 4, textAlign: 'center'}}>
                        <MDTypography color="text">
                          No templates found. Create your first template!
                        </MDTypography>
                      </Paper>
                    </Grid>
                  ) : (
                    filteredTemplates.map(template => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                        <Card
                          onClick={selectMode ? () => toggleSelectId(template.id) : undefined}
                          sx={{
                            position: 'relative',
                            height: '100%',
                            cursor: selectMode ? 'pointer' : 'default',
                            '&:hover': {boxShadow: 6},
                            outline: selectMode && selectedIds.has(template.id) ? '2px solid #1976d2' : 'none',
                          }}>
                          <MDBox
                            sx={{
                              height: 140,
                              bgcolor: 'grey.100',
                              backgroundImage: template.thumbnailUrl
                                ? `url(${template.thumbnailUrl})`
                                : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderTopLeftRadius: '4px',
                              borderTopRightRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                            }}
                            onClick={!selectMode ? () => navigate(`/section-templates/${template.id}/edit`) : undefined}>
                            {!template.thumbnailUrl && (
                              <Avatar
                                sx={{
                                  width: 60,
                                  height: 60,
                                  bgcolor: 'primary.main',
                                  fontSize: 24,
                                }}>
                                {template.name.charAt(0).toUpperCase()}
                              </Avatar>
                            )}

                            {selectMode && (
                              <Checkbox
                                checked={selectedIds.has(template.id)}
                                onChange={e => { e.stopPropagation(); toggleSelectId(template.id); }}
                                onClick={e => e.stopPropagation()}
                                sx={{position: 'absolute', top: 4, left: 4, bgcolor: 'white', borderRadius: 1, p: 0.25}}
                                size="small"
                              />
                            )}
                            {template.isSystem && (
                              <Tooltip title="System Template">
                                <Lock
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: 'warning.main',
                                    bgcolor: 'white',
                                    borderRadius: '50%',
                                    p: 0.5,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </MDBox>

                          <MDBox p={2}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start">
                              <Box>
                                <MDTypography variant="body2" fontWeight="bold">
                                  {template.name}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {template.typeKey}
                                </MDTypography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={e => handleMenuOpen(e, template)}
                                sx={{display: selectMode ? 'none' : 'inline-flex'}}>
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box
                              display="flex"
                              gap={0.5}
                              mt={1}
                              flexWrap="wrap">
                              <MDBadge
                                badgeContent={template.category}
                                color="info"
                                size="xs"
                                sx={{textTransform: 'capitalize'}}
                              />
                              {template.isLocked && (
                                <MDBadge
                                  badgeContent="Locked"
                                  color="secondary"
                                  size="xs"
                                />
                              )}
                            </Box>

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mt={2}>
                              <Tooltip title="Times used">
                                <MDTypography variant="caption" color="text">
                                  Used: {template.usageCount || 0}
                                </MDTypography>
                              </Tooltip>
                              <Tooltip title="Number of fields">
                                <MDTypography variant="caption" color="text">
                                  Fields:{' '}
                                  {template.fieldDefinitions?.length || 0}
                                </MDTypography>
                              </Tooltip>
                            </Box>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        key={selectedTemplate?.id}>
        <MenuItem
          onClick={() => {
            navigate(`/section-templates/${selectedTemplate?.id}/edit`);
            handleMenuClose();
          }}>
          <Edit fontSize="small" sx={{mr: 1}} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicateClick}>
          <ContentCopy fontSize="small" sx={{mr: 1}} /> Duplicate
        </MenuItem>
        <MenuItem onClick={handleExport}>
          <Upload fontSize="small" sx={{mr: 1}} /> Export
        </MenuItem>
        {!selectedTemplate?.isSystem && (
          <MenuItem onClick={handleDelete} sx={{color: 'error.main'}}>
            <Delete fontSize="small" sx={{mr: 1}} /> Delete
          </MenuItem>
        )}
      </Menu>

      <DuplicateNoticeDialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setTemplateToDuplicate(null); 
        }}
        onConfirm={handleDuplicateConfirm}
        template={templateToDuplicate}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default SectionTemplateList;
